import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { DAKAR_IMAGE } from "@/lib/constants";
import api from "@/lib/api";
import { Loader2, ArrowRight } from "lucide-react";

const GROUPS = [
  { key: "presidence", label: "Présidence" },
  { key: "executif", label: "Bureau exécutif" },
  { key: "conseillers", label: "Conseillers stratégiques" },
  { key: "commissions", label: "Responsables de commissions" },
  { key: "sages", label: "Comité des Sages" },
];

const initials = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const PortraitCard = ({ member, index }) => {
  const roles = (member.role || "").split("\n").map((r) => r.trim()).filter(Boolean);
  return (
    <div data-testid={`bureau-member-${index}`} className="flex flex-col group">
      <div className="aspect-[3/4] overflow-hidden bg-gradient-to-br from-brand-blue to-brand-green shadow-md">
        {member.photo_url ? (
          <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/90 text-6xl font-bold tracking-wide">
            {initials(member.name)}
          </div>
        )}
      </div>
      <h3 className="mt-5 text-center text-lg font-extrabold uppercase tracking-tight text-slate-900 leading-tight">
        {member.name}
      </h3>
      <div className="mt-2 text-center space-y-1.5">
        {roles.map((r, i) => (
          <p key={i} className="text-sm font-semibold text-brand-green leading-snug">{r}</p>
        ))}
      </div>
      {member.bio && <p className="mt-3 text-center text-xs text-slate-500 leading-relaxed">{member.bio}</p>}
    </div>
  );
};

export const BureauContent = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("presidence");

  useEffect(() => {
    api.get("/bureau").then(({ data }) => setMembers(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const byGroup = useMemo(() => {
    const map = {};
    members.forEach((m) => { (map[m.group] ||= []).push(m); });
    return map;
  }, [members]);

  const available = useMemo(() => GROUPS.filter((g) => (byGroup[g.key] || []).length > 0), [byGroup]);

  useEffect(() => {
    if (available.length && !available.find((g) => g.key === selected)) {
      setSelected(available[0].key);
    }
  }, [available, selected]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-brand-blue" size={32} /></div>;
  if (members.length === 0) return <div className="py-24 text-center text-slate-500">Les membres du bureau seront bientôt présentés ici.</div>;

  const currentGroup = available.find((g) => g.key === selected) || available[0];
  const list = byGroup[currentGroup.key] || [];

  return (
    <div data-testid="bureau-content" className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
      {/* Main */}
      <div className="lg:col-span-9 order-2 lg:order-1">
        <h2 className="text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-brand-blue mb-10">
          {currentGroup.label}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {list.map((m, i) => <PortraitCard key={m.id} member={m} index={`${currentGroup.key}-${i}`} />)}
        </div>
      </div>

      {/* Sidebar */}
      <aside className="lg:col-span-3 order-1 lg:order-2 lg:border-l lg:border-border lg:pl-8">
        <p className="text-sm font-extrabold uppercase tracking-wide text-slate-900">Les instances</p>
        <nav className="mt-4 flex lg:flex-col gap-x-4 gap-y-3 flex-wrap">
          {available.map((g) => (
            <button
              key={g.key}
              data-testid={`bureau-group-tab-${g.key}`}
              onClick={() => setSelected(g.key)}
              className={`text-left text-base transition-colors ${
                g.key === currentGroup.key ? "text-brand-blue font-bold" : "text-slate-600 hover:text-brand-blue"
              }`}
            >
              {g.label}
            </button>
          ))}
        </nav>
      </aside>
    </div>
  );
};

export default function Bureau() {
  return (
    <div data-testid="bureau-page">
      <SEO title="Le bureau" description="Découvrez le Comité de Pilotage (COPIL) de DEFAR - Les Bâtisseurs : la présidence, le bureau exécutif, les conseillers et les commissions." />
      <PageHero
        overline="Organisation"
        title="Le bureau du mouvement"
        subtitle="Le Comité de Pilotage (COPIL) qui anime et coordonne l'action de DEFAR - Les Bâtisseurs."
        image={DAKAR_IMAGE}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <BureauContent />
      </section>

      <section className="bg-brand-blue py-16 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white">Rejoignez les Bâtisseurs</h2>
          <p className="mt-4 text-blue-100">Engagez-vous aux côtés du bureau pour bâtir une Nation souveraine, juste et solidaire.</p>
          <Link to="/adhesion" className="mt-8 inline-flex items-center gap-2 bg-brand-green text-white hover:bg-brand-greenhover transition-colors px-8 py-4 font-semibold">
            Rejoindre DEFAR <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
